<template>
    <div>
        <DCard
            padding
        >
            <div class="mb-5">
                <h4>
                    {{ __('tools.sms.create') }} "{{ __(`tools.sms.templates.${removeSuffixFromString(template.name)}.name`) }}"
                </h4>
                <span class="text-muted fst-italic">
                    {{ __(`tools.sms.templates.${removeSuffixFromString(template.name)}.description`) }}
                </span>
            </div>
            <DForm @submit.prevent>
                <DRow>
                    <DCol col="7">
                        <DFormRow
                            class="mb-4"
                            is-vertical
                            :label="__('tools.sms.brand')"
                            is-required
                            :error="errors.brand"
                        >
                            <DSelect
                                v-model="form.brand"
                                :options="brandOptions"
                                @update:value="updateSelectedBrand"
                            />
                        </DFormRow>
                        <DFormRow
                            class="mb-4"
                            is-vertical
                            :label="__('tools.sms.language')"
                            is-required
                            :error="errors.language_code"
                        >
                            <DSelect
                                :options="languages"
                                :model-value="form.language_code"
                                @update:value="handleLanguageChange"
                            />
                        </DFormRow>
                        <DFormRow
                            class="mb-4"
                            is-vertical
                            :label="__('tools.sms.content')"
                            is-required
                            :error="errors.content"
                            :hint="contentLength + '/459 characters'"
                        >
                            <DFormTextarea
                                id="content"
                                v-model="form.content"
                                rows="15"
                                @update:modelValue="contentLengthUpdate"
                            />
                        </DFormRow>
                    </DCol>
                    <DCol col="5">
                        <h2 class="h5">
                            {{ __('tools.emails.footer_legal_info.legend') }}
                        </h2>
                        <EmailPlaceholders :placeholders="placeholders" />
                    </DCol>
                </DRow>
            </DForm>
            <DFabs>
                <DFab
                    id="btn-save"
                    icon="eye"
                    :label="__('messages.preview')"
                    @click="openPreviewModal"
                />
            </DFabs>
        </DCard>
        <PreviewContentModal
            v-if="showPreviewModal"
            :show-modal="showPreviewModal"
            :sms-type="form.smsType"
            :content="form.content"
            @submit="submitForm"
            @closeModal="closePreviewModal"
        />
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import EmailPlaceholders from '~@/Components/Tools/Emails/Placeholders'
import PreviewContentModal from '~@/Components/Tools/Sms/PreviewContentModal'

const props = defineProps({
    template: {
        type: Object,
        default: () => { return {} }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    },
    brands: {
        type: Array,
        default: () => { return [] }
    },
    availableLanguages: {
        type: Array,
        default: () => { return [] }
    },
    allLanguages: {
        type: Array,
        default: () => { return [] }
    },
    placeholders: {
        type: Object,
        default: () => { return {} }
    }
})

const showPreviewModal = ref(false)

function openPreviewModal () {
    showPreviewModal.value = true
}

function closePreviewModal () {
    showPreviewModal.value = false
}

const form = useForm({
    brand: null,
    content: props.template.content,
    smsType: removeSuffixFromString(props.template.name),
    language_code: null
})

const languages = ref([])
const contentLength = ref(form.content?.length ?? 0)

onMounted(() => {
    languages.value = getAvailableLanguages()
    form.language_code = null
})

const brandOptions = computed(() => {
    const options = props.brands.map((brand) => {
        return {
            label: brand.name,
            value: brand.id
        }
    })

    options.unshift({
        label: '',
        value: null,
        disabled: true,
        hidden: true,
        selected: true
    })

    return options
})

function handleLanguageChange (item) {
    form.language_code = item
}
function getAvailableLanguages () {
    const lang = []

    if (!form.brand) {
        return props.allLanguages
    }

    props.availableLanguages.forEach((item) => {
        if (item.brand_id === form.brand) {
            lang.push(item.languages)

            item.languages.forEach((langValue) => {
                if (langValue.value === item.default_customer_language) {
                    form.language_code = item.default_customer_language
                }
            })
        }
    })

    return lang[0]
}

function updateSelectedBrand (item) {
    form.brand = parseInt(item)
    languages.value = getAvailableLanguages()
}

function removeSuffixFromString (inputString) {
    const regex = /(_\d+)?__\w{2}$/

    return inputString.replace(regex, '')
}

function contentLengthUpdate () {
    contentLength.value = form.content.length
}

function submitForm () {
    form.post(route('tools.sms-templates.store'))
}
</script>
